
import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationItem } from '@/components/notifications/NotificationItem';
import { useAuth } from '@/contexts/auth';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Check, RefreshCw } from 'lucide-react';

export const NotificationsBell = () => {
  const { user } = useAuth();
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAllAsRead,
    markAsRead,
    refreshNotifications,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [hasFetchedInitial, setHasFetchedInitial] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Run notification refresh once when user loads the component
  useEffect(() => {
    if (user && !hasFetchedInitial) {
      console.log("Performing initial notification refresh for user:", user.id);
      setHasFetchedInitial(true);
      
      // Refresh notifications
      refreshNotifications(false)
        .then((notifications) => {
          console.log(`Initial refresh complete, found ${notifications.length} notifications`);
        })
        .catch(error => {
          console.error('Error during notification refresh cycle:', error);
        });
    }
  }, [user, hasFetchedInitial, refreshNotifications]);
  
  // Set up periodic auto refresh (every 30 seconds)
  useEffect(() => {
    if (!user?.id) return;
    
    // Setup automatic refresh every 30 seconds
    const autoRefreshTimer = setInterval(() => {
      refreshNotifications(true);
    }, 30000);
    
    return () => {
      clearInterval(autoRefreshTimer);
    };
  }, [user?.id, refreshNotifications]);
  
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Refresh notifications when opening the popover
      setIsRefreshing(true);
      refreshNotifications(false)
        .then(() => {
          setIsRefreshing(false);
        })
        .catch(error => {
          setIsRefreshing(false);
          console.error('Error refreshing notifications:', error);
        });
    }
  };
  
  const handleManualRefresh = () => {
    setIsRefreshing(true);
    refreshNotifications(false)
      .then(() => {
        setIsRefreshing(false);
        toast.success('Notifications refreshed');
      })
      .catch(error => {
        setIsRefreshing(false);
        console.error('Error refreshing notifications:', error);
        toast.error('Could not refresh notifications');
      });
  };
  
  const handleMarkAllAsRead = () => {
    markAllAsRead()
      .then(() => {
        toast.success('All notifications marked as read');
      })
      .catch(error => {
        console.error('Error marking all as read:', error);
        toast.error('Could not mark all notifications as read');
      });
  };

  if (!user) return null;
  
  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          aria-label="Notifications"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[350px] max-h-[450px] overflow-y-auto p-0">
        <div className="p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-lg">Notifications</h3>
            <Button 
              variant="outline" 
              size="sm" 
              className="text-xs"
              onClick={handleManualRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw size={12} className={`mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
          
          {unreadCount > 0 && (
            <div className="mt-2 flex items-center justify-between">
              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                {unreadCount} unread {unreadCount === 1 ? 'notification' : 'notifications'}
              </Badge>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs flex items-center" 
                onClick={handleMarkAllAsRead}
                disabled={loading || unreadCount === 0}
              >
                <Check size={12} className="mr-1" />
                Mark all as read
              </Button>
            </div>
          )}
        </div>
        
        {error ? (
          <div className="py-4 px-4">
            <Alert variant="destructive" className="mb-2">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <div className="mt-2 text-center">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => refreshNotifications(false)}
                disabled={loading}
              >
                Try Again
              </Button>
            </div>
          </div>
        ) : loading ? (
          <div className="py-8 text-center text-gray-500">
            <div className="flex flex-col items-center">
              <RefreshCw size={20} className="animate-spin mb-2" />
              <span>Loading notifications...</span>
            </div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            No notifications yet
          </div>
        ) : (
          <div className="divide-y">
            {notifications.map(notification => (
              <NotificationItem 
                key={notification.id} 
                notification={notification}
                onRead={markAsRead}
              />
            ))}
          </div>
        )}
        
        {notifications.length > 0 && (
          <div className="p-3 border-t sticky bottom-0 bg-white">
            <div className="text-xs text-gray-500 text-right">
              {notifications.length} {notifications.length === 1 ? 'notification' : 'notifications'}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
